<template>
  <div class="location-page">
    <SiteHeader />

    <section class="hero">
      <div class="wrap">
        <p class="kicker mono">Local Service Area</p>
        <h1 class="display">Laptop Repair Service in {{ location.name }}, Bengaluru</h1>
        <p class="intro">{{ location.intro }}</p>
        <a href="#contact" class="cta">
          <span>Book a repair</span>
          <span class="arw">→</span>
        </a>
      </div>
    </section>

    <section class="services">
      <div class="wrap">
        <p class="kicker mono">What we fix</p>
        <ol class="ledger">
          <li v-for="(s, i) in location.services" :key="s" class="row">
            <span class="n mono">{{ String(i + 1).padStart(2, '0') }}</span>
            <span class="svc">{{ s }}</span>
          </li>
        </ol>
      </div>
    </section>

    <section class="brands">
      <div class="wrap">
        <p class="kicker mono">Brands serviced</p>
        <p class="brand-list">HP · Dell · Lenovo · Asus · Acer · MacBook</p>
        <p class="disclaimer">Nexxt Gen Computers is an independent repair centre and is not affiliated with or authorised by Apple Inc. or any other brand named above.</p>
      </div>
    </section>

    <section class="amc">
      <div class="wrap">
        <p class="kicker mono">For businesses</p>
        <p class="amc-copy">We offer Annual Maintenance Contracts (AMC) and corporate/bulk repair for businesses, schools and coworking spaces{{ location.isCorePickupArea ? ' in ' + location.name : '' }}, with transparent pricing shared before any work begins and a GST invoice on every job.</p>
      </div>
    </section>

    <section class="pickup" v-if="location.isCorePickupArea">
      <div class="wrap">
        <p class="pickup-copy">Free pickup and drop is available for laptops anywhere in {{ location.name }}. Call us to schedule a collection.</p>
      </div>
    </section>

    <SiteFooter />
  </div>
</template>

<script>
import SiteHeader from './SiteHeader.vue'
import SiteFooter from './SiteFooter.vue'

export default {
  name: 'LocationPage',
  components: { SiteHeader, SiteFooter },
  props: {
    location: { type: Object, required: true }
  },
  beforeMount() {
    this.applyMeta()
  },
  watch: {
    location() { this.applyMeta() }
  },
  methods: {
    ensureMeta(selector, attr, value) {
      let tag = document.querySelector(selector)
      if (!tag) {
        tag = document.createElement('meta')
        tag.setAttribute(attr, value)
        document.head.appendChild(tag)
      }
      return tag
    },
    upsertMeta(name, content, property = false) {
      const attr = property ? 'property' : 'name'
      const selector = `meta[${attr}="${name}"]`
      const tag = this.ensureMeta(selector, attr, name)
      tag.setAttribute('content', content)
    },
    applyMeta() {
      const loc = this.location
      if (!loc) return
      const title = loc.metaTitle
      const description = loc.metaDescription
      document.title = loc.metaTitle
      this.upsertMeta('description', description)
      this.upsertMeta('og:title', title, true)
      this.upsertMeta('og:description', description, true)
      this.upsertMeta('twitter:title', title)
      this.upsertMeta('twitter:description', description)

      const url = `https://nexxtgencomputers.in/laptop-repair-${loc.slug}`
      let canonical = document.querySelector('link[rel="canonical"]')
      if (!canonical) {
        canonical = document.createElement('link')
        canonical.setAttribute('rel', 'canonical')
        document.head.appendChild(canonical)
      }
      canonical.setAttribute('href', url)

      let ogUrl = document.querySelector('meta[property="og:url"]')
      if (ogUrl) ogUrl.setAttribute('content', url)
      this.upsertMeta('twitter:url', url)

      let ld = document.getElementById('location-schema')
      if (!ld) {
        ld = document.createElement('script')
        ld.type = 'application/ld+json'
        ld.id = 'location-schema'
        document.head.appendChild(ld)
      }
      ld.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Service',
        '@id': `${url}#service`,
        name: `Laptop Repair in ${loc.name}, Bengaluru`,
        serviceType: 'Laptop and computer repair',
        areaServed: {
          '@type': 'Place',
          name: `${loc.name}, Bengaluru`
        },
        provider: {
          '@type': 'ComputerRepairService',
          '@id': 'https://nexxtgencomputers.in/#business'
        },
        url,
        description
      })
    }
  }
}
</script>

<style scoped>
.location-page { background: var(--ink); color: var(--text-on-ink); }
.wrap { width: min(1360px, 94vw); margin: 0 auto; }

.hero { padding: clamp(120px, 14vw, 180px) 0 clamp(50px, 6vw, 80px); }
.kicker { color: var(--coral); font-size: 12px; letter-spacing: 3px; text-transform: uppercase; margin: 0 0 18px; }
.hero .display { font-family: var(--font-display); font-weight: 700; letter-spacing: -2px; line-height: 1.05;
  font-size: clamp(28px, 4.5vw, 56px); margin: 0 0 24px; max-width: 18ch; }
.intro { font-size: clamp(15px, 1.4vw, 18px); line-height: 1.68; color: #a6a6ad; max-width: 62ch; margin: 0 0 32px; }
.cta { display: inline-flex; align-items: center; gap: 12px; text-decoration: none;
  color: var(--text-on-ink); font-family: var(--font-mono); font-size: 13px; letter-spacing: 2px;
  text-transform: uppercase; padding-bottom: 5px; border-bottom: 1px solid var(--text-on-ink); }
.cta .arw { transition: transform 0.35s var(--ease); }
.cta:hover { color: var(--coral); border-color: var(--coral); }
.cta:hover .arw { transform: translateX(6px); }

.services, .brands, .amc { padding: clamp(30px, 4vw, 50px) 0; border-top: 1px solid #2c2c32; }
.ledger { list-style: none; margin: 0; padding: 0; }
.row { display: grid; grid-template-columns: 46px 1fr; align-items: center; column-gap: 18px;
  padding: 14px 0; border-bottom: 1px solid #2c2c32; }
.row .n { color: var(--coral); font-size: 12px; }
.row .svc { font-family: var(--font-display); font-weight: 600; letter-spacing: -0.5px; font-size: clamp(18px, 2vw, 26px); }

.brand-list { font-family: var(--font-display); font-weight: 500; font-size: clamp(16px, 1.6vw, 22px); margin: 0 0 12px; }
.disclaimer { font-size: 12px; color: #6a6a70; line-height: 1.6; max-width: 62ch; margin: 0; }

.amc-copy, .pickup-copy { font-size: clamp(14px, 1.3vw, 17px); line-height: 1.68; color: #a6a6ad; max-width: 62ch; margin: 0; }
.pickup { padding: clamp(20px, 3vw, 30px) 0; }
.pickup-copy { color: var(--coral); font-weight: 500; }
</style>
